import { useEffect, useMemo } from 'react';
import { Table, Typography, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { createUseStyles } from 'react-jss';
import type {
  SimulationDetail,
  SimulationContextMapping,
  SimulationFulfillmentContextMappingTypes,
} from '../../store/simulation/types';
import { useStoreState, useStoreActions } from '../../store';
import type { ShipiumTagConfig } from '../../components/shared/ShipiumTag';
import { ShipiumTagWithConfig } from '../../components/shared/ShipiumTag';

const { Text } = Typography;
const useStyles = createUseStyles({
  tableStyles: {
    '&& table > .ant-table-thead > tr > th': {
      fontWeight: 'bold',
    },
    '&& table > .ant-table-tbody > tr > td': {
      fontFamily: 'monospace',
      fontSize: '0.9em',
    },
  },
});

export interface SimulationMappingsTableProps {
  simulation: SimulationDetail;
}
export const SimulationMappingsTable = ({ simulation }: SimulationMappingsTableProps) => {
  const loading = useStoreState((state) => state.loading.loadingFlags.fulfillmentContext);
  const fulfillmentContextDetails = useStoreState((state) => state.FP_fulfillmentContext.fulfillmentContextDetails);
  const fetchFulfillmentContextDetailByContextId = useStoreActions(
    (state) => state.FP_fulfillmentContext.fetchFulfillmentContextDetailByContextId,
  );

  const simulationMappings = useMemo(() => {
    return simulation?.simulationContextMappings || [];
  }, [simulation]);

  const contextIds = useMemo(() => {
    return simulationMappings?.map((mapping) => mapping.simulationFulfillmentContextId);
  }, [simulationMappings]);

  useEffect(() => {
    if (contextIds.length > 0) {
      for (const contextId of contextIds) {
        if (contextId !== '') {
          fetchFulfillmentContextDetailByContextId(contextId);
        }
      }
    }
  }, [contextIds, fetchFulfillmentContextDetailByContextId]);

  const classes = useStyles();

  const shipiumTagMapStatus: Record<SimulationFulfillmentContextMappingTypes, ShipiumTagConfig> = {
    'context-id': { color: 'Blue', label: 'Fulfillment Context Mapping', dark: true, icon: 'config' },
    'historical-context': { color: 'Coral', label: 'Historical Context', dark: true, icon: 'config' },
    'origin-postal-exact': { color: 'Fuschia', label: 'Origin Postal Exact', dark: true, icon: 'config' },
    'destination-postal-partial': { color: 'Fuschia', label: 'Destination Postal Partial', dark: true, icon: 'config' },
    'origin-postal-partial': { color: 'Fuschia', label: 'Origin Postal Parital', dark: true, icon: 'config' },
    'destination-postal-exact': { color: 'Fuschia', label: 'Destination Postal Exact', dark: true, icon: 'config' },
    'context-search': { color: 'Gold', label: 'Pass Through', dark: true, icon: 'config' },
  };

  const columns: ColumnsType<SimulationContextMapping> = [
    {
      title: 'Mapping Type',
      key: 'postal',
      align: 'left',
      width: 300,
      render: (val: SimulationContextMapping) => {
        return <ShipiumTagWithConfig configMap={shipiumTagMapStatus} tag={val.mappingType} />;
      },
    },
    {
      title: 'Mapping Values',
      key: 'postal',
      align: 'left',
      width: 300,
      render: (val: SimulationContextMapping) => {
        return (
          <Space direction="vertical" size={'small'}>
            {val.mappingValue?.postalCode && <Text>Postal Code: {val.mappingValue?.postalCode}</Text>}
            {val.mappingValue?.countryCode && <Text>Country Code: {val.mappingValue?.countryCode}</Text>}
            {val.mappingValue?.originalFulfillmentContextId && (
              <Text>Fulfillment Context Id: {val.mappingValue?.originalFulfillmentContextId}</Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Simulation Fulfillment Context',
      key: 'context',
      align: 'left',
      width: 300,
      render: (val: SimulationContextMapping) => {
        const context = fulfillmentContextDetails[val.simulationFulfillmentContextId];
        if (context) {
          return (
            <Space direction="vertical" size={'small'}>
              <Text>{context.fulfillmentContext.name}</Text>
              <Text type={'secondary'}>
                {context.origin.origin.name} - {context.origin.address.address.location?.city}{' '}
                {context.origin.address.address.location?.region} {context.origin.address.address.location?.postalCode}
              </Text>
            </Space>
          );
        } else if (!val.simulationFulfillmentContextId) {
          return <Text type={'warning'}>Passthrough</Text>;
        }
      },
    },
  ];

  const data = simulationMappings?.map((mapping: any, index) => {
    mapping.key = mapping.mappingKey;
    return mapping;
  });

  return loading ? null : (
    <Table
      loading={loading}
      size={'small'}
      columns={columns}
      dataSource={data}
      pagination={{ pageSize: 24, showSizeChanger: false }}
      bordered={false}
      className={classes.tableStyles}
    />
  );
};
